import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { authService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';
import ForgotPasswordModal from './ForgotPasswordModal';

const LoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email or username is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    // Set rememberMe BEFORE signIn so supabase auth storage picks the right backend
    // (localStorage when remembered, sessionStorage otherwise — see src/lib/supabase.js)
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }

    try {
      const rawInput = formData?.email?.trim();
      // ถ้าเป็น email → คงตัวอักษรเดิม (Supabase จัดการ normalize เอง)
      // ถ้าเป็น username → บังคับเป็นตัวพิมพ์เล็กทั้งหมด (DB เก็บเป็น lowercase)
      const identifier = isEmail(rawInput) ? rawInput : rawInput?.toLowerCase();
      let emailToUse = identifier;

      // ถ้าไม่ใช่ email format → ถือว่าเป็น username → ค้นหา email ผ่าน RPC (bypass RLS)
      if (!isEmail(identifier)) {
        const { data: emailResult, error: rpcError } = await supabase
          .rpc('get_email_by_username', { p_username: identifier });

        if (rpcError || !emailResult) {
          setErrors({ email: 'Invalid username or password.' });
          setLoading(false);
          return;
        }
        emailToUse = emailResult;
      }

      const { data, error } = await authService?.signIn(emailToUse, formData?.password);

      if (error) {
        const msg = error?.message?.toLowerCase() || '';

        // ถ้า Supabase บอก email ยังไม่ confirm → redirect ไป verify + สร้าง code ใหม่
        if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
          localStorage.setItem('pending_verification_email', emailToUse);

          navigate('/email-verification', {
            state: { email: emailToUse },
          });
          setLoading(false);
          return;
        }

        setErrors({ email: isEmail(identifier) ? 'Invalid email or password.' : 'Invalid username or password.' });
        setLoading(false);
        return;
      }

      if (data?.user) {
        // Set session cookie
        //  - rememberMe: persistent cookie, 30-day Max-Age
        //  - otherwise: session cookie (cleared when browser closes)
        // Secure flag auto-applied on HTTPS
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        const secureFlag = isHttps ? '; Secure' : '';
        const maxAge = rememberMe ? `; Max-Age=${60 * 60 * 24 * 30}` : '';
        document.cookie = `session_active=1; path=/; SameSite=Lax${secureFlag}${maxAge}`;

        // เช็ค status จาก user_profiles ก่อน redirect
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role, status')
          .eq('id', data.user.id)
          .single();

        // ถ้ายังไม่ verify email → redirect ไป /email-verification
        if (profile?.status === 'pending') {
          localStorage.setItem('pending_verification_email', data.user.email);

          navigate('/email-verification', {
            state: { email: data.user.email },
          });
          return;
        }

        if (profile?.role === 'admin') {
          navigate('/admin-dashboard');
        } else {
          navigate('/main-dashboard');
        }
      }
    } catch (error) {
      setErrors({ email: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
          Welcome Back to<br />Roomwork.ai
        </h1>
        <p className="text-gray-400 text-sm">
          Sign in to continue crafting beautiful spaces
        </p>
      </div>

      {/* Sign Up Link */}
      <div className="mb-8">
        <p className="text-sm text-gray-400">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login-registration')}
            className="text-white hover:text-gray-300 font-medium underline transition-colors"
          >
            SIGN UP
          </button>
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <input
            type="text"
            name="email"
            placeholder="Email or Username"
            value={formData?.email}
            onChange={handleInputChange}
            autoComplete="username"
            className="w-full px-4 py-3.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)]"
          />
          {errors?.email && (
            <p className="text-red-400 text-xs mt-1.5">{errors?.email}</p>
          )}
        </div>

        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={formData?.password}
            onChange={handleInputChange}
            className="w-full px-4 py-3.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 pr-12 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)]"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
          {errors?.password && (
            <p className="text-red-400 text-xs mt-1.5">{errors?.password}</p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded border transition-all duration-200 flex items-center justify-center ${rememberMe ? 'bg-orange-500/60 border-orange-500/50' : 'bg-transparent border-[#2a2a2a]'}`}>
              {rememberMe && <Icon name="Check" size={10} className="text-white" />}
            </div>
            <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">Remember me</span>
          </label>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-gray-400 hover:text-gray-300 transition-colors"
          >
            Forgot Password?
          </button>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          className="mt-6 bg-[#2a2a2a] hover:bg-[#333333] text-white border border-[#2a2a2a] rounded-2xl py-3.5 font-medium transition-all shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] focus:outline-none focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] active:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)]"
        >
          <span className="flex items-center justify-center gap-2">
            Sign In
            <div className="w-8 h-8 rounded-full bg-[#3a3a3a] flex items-center justify-center">
              <Icon name="ArrowRight" size={16} className="text-white" />
            </div>
          </span>
        </Button>
      </form>

      {/* Terms */}
      <div className="mt-6">
        <p className="text-xs text-gray-500 leading-relaxed">
          By signing in, you agree to our{' '}
          <a href="#" className="text-gray-400 hover:text-gray-300 underline">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-gray-400 hover:text-gray-300 underline">Privacy Policy</a>.
        </p>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default LoginForm;